/**
 * GPX import pipeline (SPEC §8.1):
 * parse → LV95 → resample 10 m → swisstopo elevation (cached, fallback GPX ele) → smooth → grades/climbs/loop → save.
 * A loop must also carry on in its starting direction; an out-and-back ending at its start is ridden once.
 */
import { db } from '@/db/db';
import type { ElevationSource, ProcessedRoute, RoutePoint, Waypoint } from './model';
import { parseGpx } from './gpx';
import { insideSwitzerland, lv95ToWgs84, wgs84ToLv95 } from './lv95';
import {
  closesAsLoop,
  computeGrades,
  detectClimbs,
  resample,
  sha256Hex,
  smoothElevation,
  totalAscent,
  type PlanarPoint,
} from './geometry';
import { cachedElevations, fetchSwisstopoElevations, storeElevations, type FetchLike } from './elevation';

export type ImportStep = 'parse' | 'project' | 'resample' | 'elevation' | 'smooth' | 'save';

export interface ImportOptions {
  onProgress?: (step: ImportStep, fraction: number) => void;
  fetcher?: FetchLike;
  /** Skip swisstopo (tests / offline). */
  offline?: boolean;
  save?: boolean;
  name?: string;
}

export interface ImportResult {
  route: ProcessedRoute;
  elevationSource: ElevationSource;
  warnings: string[];
}

/** Build the processed route from LV95 vertices + elevations (shared by GPX import and the demo route). */
export function buildRoute(args: {
  id: string;
  name: string;
  d: number[];
  e: number[];
  n: number[];
  ele: number[];
  step: number;
  total: number;
  loop: boolean;
  waypoints: { name: string; lat: number; lon: number }[];
  source: ElevationSource;
  builtin?: boolean;
}): ProcessedRoute {
  const { d, e, n, step, loop } = args;
  const h = smoothElevation(args.ele, step, loop);
  const grades = computeGrades(h, step, loop);
  const points: RoutePoint[] = d.map((dist, i) => {
    const ll = lv95ToWgs84(e[i]!, n[i]!);
    return { d: dist, x: e[i]!, y: h[i]!, z: n[i]!, grade: grades[i]!, lat: ll.lat, lon: ll.lon };
  });
  const hClosed = loop ? [...h, h[0]!] : h;
  const dClosed = loop ? [...d, args.total] : d;
  const waypoints: Waypoint[] = [];
  for (const w of args.waypoints) {
    const p = wgs84ToLv95(w.lat, w.lon);
    const dist = e.map((x, i) => Math.hypot(x - p.e, n[i]! - p.n));
    const bestDist = dist.reduce((m, x) => Math.min(m, x), Infinity);
    // Where the route passes twice (out-and-back), the waypoint belongs to the first pass.
    const best = dist.findIndex((x) => x <= bestDist + 6);
    // Named waypoints become signposts if they are near the route (≤ 300 m).
    if (bestDist <= 300) waypoints.push({ name: w.name, d: d[best]!, lat: w.lat, lon: w.lon });
  }
  return {
    id: args.id,
    points,
    climbs: detectClimbs(dClosed, hClosed),
    waypoints,
    meta: {
      name: args.name,
      distanceM: args.total,
      ascentM: totalAscent(hClosed),
      loop,
      elevationSource: args.source,
      originE: e[0]!,
      originN: n[0]!,
      createdAt: Date.now(),
      ...(args.builtin ? { builtin: true } : {}),
    },
    updatedAt: Date.now(),
  };
}

export async function importGpx(xml: string, opts: ImportOptions = {}): Promise<ImportResult> {
  const progress = opts.onProgress ?? (() => undefined);
  const warnings: string[] = [];
  progress('parse', 0);
  const gpx = parseGpx(xml);
  progress('parse', 1);

  progress('project', 0);
  const planar: PlanarPoint[] = gpx.points.map((p) => {
    const { e, n } = wgs84ToLv95(p.lat, p.lon);
    return p.ele !== undefined ? { e, n, ele: p.ele } : { e, n };
  });
  const loop = closesAsLoop(planar);
  progress('project', 1);

  progress('resample', 0);
  const rs = resample(planar, 10, loop);
  if (rs.d.length < 3) throw new Error('Route zu kurz');
  const geomKey = rs.e.map((x, i) => `${x.toFixed(1)},${rs.n[i]!.toFixed(1)}`).join(';');
  const hash = (await sha256Hex(geomKey)).slice(0, 24);
  progress('resample', 1);

  progress('elevation', 0);
  let source: ElevationSource = 'gpx';
  let ele: number[] | null = await cachedElevations(hash);
  if (ele && ele.length === rs.d.length) {
    source = 'swisstopo';
  } else {
    ele = null;
    const inside = rs.e.filter((x, i) => insideSwitzerland(x, rs.n[i]!)).length / rs.e.length;
    if (!opts.offline && inside > 0.95) {
      try {
        const eo: Parameters<typeof fetchSwisstopoElevations>[2] = {
          onProgress: (f) => progress('elevation', f),
          delayMs: 150,
        };
        if (opts.fetcher) eo.fetcher = opts.fetcher;
        ele = await fetchSwisstopoElevations(rs.e, rs.n, eo);
        source = 'swisstopo';
        await storeElevations(hash, ele);
      } catch (err) {
        warnings.push(`swisstopo: ${err instanceof Error ? err.message : String(err)}`);
      }
    } else if (inside <= 0.95) {
      warnings.push('Route liegt (teilweise) ausserhalb der Schweiz – GPX-Höhen verwendet.');
    }
    if (!ele) {
      const hasEle = rs.ele.some((x) => Number.isFinite(x));
      if (!hasEle) warnings.push('Keine Höhendaten verfügbar – Route wird flach gefahren.');
      let lastGood = rs.ele.find((x) => Number.isFinite(x)) ?? 500;
      ele = rs.ele.map((x) => (Number.isFinite(x) ? (lastGood = x) : lastGood));
      source = 'gpx';
    }
  }
  progress('elevation', 1);

  progress('smooth', 0);
  const route = buildRoute({
    id: `route-${hash}`,
    name: opts.name ?? gpx.name,
    d: rs.d,
    e: rs.e,
    n: rs.n,
    ele,
    step: rs.step,
    total: rs.total,
    loop,
    waypoints: gpx.waypoints,
    source,
  });
  progress('smooth', 1);

  if (opts.save !== false) {
    progress('save', 0);
    await db.routes.put(route);
    progress('save', 1);
  }
  return { route, elevationSource: source, warnings };
}
